import * as R from 'ramda';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { ProgressBar, ParagraphTertiary, InformationBlockLabelDefault } from '@klarna/bubble-ui';
import { constants } from 'mage-components';
import { components as coreComponents } from 'mage-core';
import { Message, selectors as i18nSelectors } from 'mage-i18n';
import { thunks as stateThunks } from 'mage-state';
import { LanguageSelector, MOET_CONSTANTS } from 'moet-core';

import { getFrontendTypeFromQueryParam, isWidget } from '@/selectors/widget';
import {
  getMerchantCountryKey,
  getMerchantStatusValue,
  isManagedMerchant,
  isNewSplitLayout,
  isPreAssessmentMerchant,
} from '@/selectors/merchant';
import { TOP_BAR_HEIGHT } from '@/constants/styles';

const {
  WIDGET_TYPE: { MANAGED_KYC, MANAGED_RISK, MANAGED_SME_RISK },
  ONBOARDING_STATUS_VALUES_V2: { TRANSACTIONS_ENABLED },
} = MOET_CONSTANTS;

const styles = {
  container: ({ isNewSplitLayout, isWidget, statusValue }) => ({
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    left: 0,
    top:
      isWidget || (isNewSplitLayout && statusValue < TRANSACTIONS_ENABLED)
        ? 0
        : constants.MP_UI_TOPBAR_HEIGHT,
    width: '100%',
    height: TOP_BAR_HEIGHT,
    justifyContent: 'space-around',
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E8',
    zIndex: 1,
    backgroundColor: '#FFFFFF',
  }),
  progressBarStyle: {
    width: 220,
  },
  stepTextStyle: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  badge: {
    float: 'left',
    paddingLeft: '5px',
  },
};
const tPrefix = 'products-fe.components.topbar';

const getTopBarPercentage = (currentStep, maxStep) => {
  if (currentStep === 1) {
    return 5;
  }
  if (currentStep >= maxStep) {
    return 100;
  }
  return (100 * (currentStep - 1)) / maxStep;
};

export const ProgressIndicator = ({ currentStep, maxStep, isPreAssessmentMerchant }) => {
  // Don't show progress indicator if current step is 0 or if there are less than 2 steps in total.
  if (!currentStep || currentStep === 0 || !maxStep || maxStep < 2) {
    return (
      <div>
        <div style={styles.stepTextStyle} />
      </div>
    );
  }

  return (
    <div>
      <div style={styles.stepTextStyle}>
        <ParagraphTertiary bold>
          <Message path={`${tPrefix}.title`} />
        </ParagraphTertiary>
        {!isPreAssessmentMerchant && (
          <ParagraphTertiary bold>{`${currentStep}/${maxStep}`}</ParagraphTertiary>
        )}
      </div>
      <div style={styles.progressBarStyle}>
        <ProgressBar percentage={getTopBarPercentage(currentStep, maxStep)} />
      </div>
    </div>
  );
};

ProgressIndicator.propTypes = {
  isPreAssessmentMerchant: PropTypes.bool.isRequired,
  currentStep: PropTypes.number,
  maxStep: PropTypes.number,
};

export const TopBar = ({
  isManagedMerchant,
  isNewSplitLayout,
  isPreAssessmentMerchant,
  isWidget,
  language,
  merchantCountry,
  productNames = [],
  saveLanguage,
  statusValue,
  widgetType = '',
  userId,
  ...props
}) => {
  return (
    <div style={styles.container({ isNewSplitLayout, isWidget, statusValue })}>
      <ProgressIndicator {...props} isPreAssessmentMerchant={isPreAssessmentMerchant} />
      <div>
        {productNames.map((name, index) => (
          <div style={styles.badge} key={`topbar-products-${index}`}>
            <InformationBlockLabelDefault>{name}</InformationBlockLabelDefault>
          </div>
        ))}
      </div>
      {widgetType && [MANAGED_KYC, MANAGED_RISK, MANAGED_SME_RISK].includes(widgetType) && (
        <div style={styles.badge}>
          <InformationBlockLabelDefault>
            <Message path={`${tPrefix}.widget_type.${widgetType}`} />
          </InformationBlockLabelDefault>
        </div>
      )}
      {isWidget && !isPreAssessmentMerchant && (
        <div style={styles.badge}>
          <LanguageSelector
            language={language}
            saveLanguage={language => saveLanguage(userId, language)}
          />
        </div>
      )}
    </div>
  );
};

TopBar.propTypes = {
  isManagedMerchant: PropTypes.bool.isRequired,
  isNewSplitLayout: PropTypes.bool.isRequired,
  isPreAssessmentMerchant: PropTypes.bool.isRequired,
  isWidget: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  saveLanguage: PropTypes.func.isRequired,
  statusValue: PropTypes.number.isRequired,
  currentStep: PropTypes.number,
  maxStep: PropTypes.number,
  productNames: PropTypes.array,
  userId: PropTypes.string,
  widgetType: PropTypes.string,
};

export default R.compose(
  coreComponents.withTokenHelper(tokenHelper => ({
    userId: tokenHelper.getUserId(),
  })),
  connect(
    state => ({
      isManagedMerchant: isManagedMerchant(state),
      isPreAssessmentMerchant: isPreAssessmentMerchant(state),
      isWidget: isWidget(state),
      language: i18nSelectors.language(state),
      merchantCountry: getMerchantCountryKey(state),
      isNewSplitLayout: isNewSplitLayout(state),
      statusValue: getMerchantStatusValue(state),
      widgetType: getFrontendTypeFromQueryParam(state),
    }),
    {
      saveLanguage: stateThunks.saveLanguage,
    },
  ),
)(TopBar);
